import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Operation, StorageService } from '@factor_ec/utils';
import { MessageService } from '@factor_ec/ui';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import { Simulation } from '@/main/models/simulation';
import { SimulationComponent } from '@/main/components/simulation/simulation.component';
import { SessionService } from '@/core/session.service';
import { ApiService } from '@/core/api.service';
import { SimulationDetailComponent } from '../components/simulation-detail/simulation-detail.component';
import { ActivateCreditComponent } from '../components/activate-credit/activate-credit.component';
import { ProgressService } from '@/shared/progress.service';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { AppService } from '@/core/app.service';
import { Group } from '../models/group';

/**
 * @description Servicio para gestionar las simulaciones de crédito
 * @class SimulationService
 */
@Injectable({ providedIn: 'root' })
export class SimulationService {
  // Dependency injection
  private readonly sessionService = inject(SessionService);
  private readonly appService = inject(AppService);
  private readonly apiService = inject(ApiService);
  private readonly storageService = inject(StorageService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly progressService = inject(ProgressService);

  // Events
  public readonly change: EventEmitter<Operation> = new EventEmitter();

  /**
   * @description Activa una simulación de crédito
   * @param {Partial<Simulation>} simulation - Simulación a activar
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  active(simulation?: Partial<Simulation>): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      if (!moment(simulation?.effectiveDate).isAfter(moment())) {
        const dialogRef = this.dialog.open(ActivateCreditComponent, {
          data: { simulation },
          panelClass: ['ft-dialog', 'ft-dialog--stacked'],
          height: '100vh',
          width: '682px',
          position: { left: 'auto', right: '0' }
        });
        dialogRef.componentInstance.afterSubmit.subscribe(
          (operation: Operation) => {
            resolve(operation);
          }
        );
      } else {
        this.messageService
          .show(
            $localize`The selected offer has a future start date and cannot be activated. Do you want to edit the offer?`,
            {
              type: 'modal',
              class: 'text-center d-flex flex-column align-items-center gap-3',
              icon: {
                name: 'warning',
                class: 'text-warning ft-icon--4',
                collection: 'factoricons-slim'
              },
              actions: [
                {
                  label: $localize`Cancel`,
                  value: 0,
                  type: 'stroked',
                  class: 'flex-grow-1'
                },
                {
                  label: $localize`Accept`,
                  value: 1,
                  type: 'flat',
                  class: 'flex-grow-1'
                }
              ]
            }
          )
          .subscribe(value => {
            if (value === 1) {
              this.open(simulation, undefined ,'edit');
            }
          });
      }
    });
  }

  /**
   * @description Elimina una simulación
   * @param {Simulation} simulation - Simulación a eliminar
   * @returns {Promise<void>} Promesa que resuelve cuando se completa la eliminación
   */
  async remove(simulation: Simulation): Promise<void> {
    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Are you sure you want to delete the offer #${simulation.operationNumber}?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'error',
            class: 'text-danger ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/offer/delete-offer/{operationNumber}/${simulation.countryId}/COM`,
        method: 'DELETE',
        useToken: true,
        pathParams: {
          operationNumber: {
            value: simulation.operationNumber,
            encrypted: true
          }
        }
      });
      this.change.emit({ method: 'update', entity: simulation });
      this.messageService.show($localize`Offer deleted successfully.`, {
        class: 'ft-message--success',
        icon: 'check--circle'
      });
    }
    return value;
  }

  /**
   * @description Abre el diálogo de simulación
   * @param {Partial<Simulation>} simulation - Simulación a editar
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  open(
    simulation?: Partial<Simulation>,
    group?: Group,
    mode: 'view' | 'edit' = 'view'
  ): Promise<Operation> {
    const config = {
      data: {
        simulation,
        group
      },
      panelClass: ['ft-dialog', 'ft-dialog--stacked'],
      height: '100vh',
      width: '682px',
      position: { left: 'auto', right: '0' }
    };
    return new Promise<Operation>(resolve => {
      if (simulation && mode === 'view') {
        this.dialog.open(SimulationDetailComponent, config);
      } else {
        const dialogRef = this.dialog.open(SimulationComponent, config);
        dialogRef.componentInstance.afterSubmit.subscribe(
          (operation: Operation) => {
            this.change.emit(operation);
            resolve(operation);
          }
        );
      }
    });
  }

  /**
   * @description Obtiene las acciones contextuales disponibles para una simulación
   * @param {Simulation} simulation - Simulación objetivo
   * @returns {any[]} Array de acciones disponibles
   */
  getContextActions(simulation: Simulation): any[] {
    let actions: any[];
    if (!simulation?.operationNumber && !simulation?.operation.number) {
      actions = [];
    } else {
      actions = [
        {
          id: 'update',
          type: 'group',
          children: [
            {
              id: 'editOffer',
              label: $localize`Edit offer`,
              visible: this.sessionService.isGranted('btn-mnu-edit-offer'),
              click: () => {
                this.open(simulation, undefined ,'edit');
              }
            },
            {
              id: 'activateOffer',
              label: $localize`Activate credit`,
              visible: this.sessionService.isGranted('btn-mnu-activate-credit'),
              click: async () => {
                const currencyId = this.storageService.get(
                  this.appService.currencyId,
                  'local'
                );
                if (!this.appService.restrictions()?.maxCreditAmount) {
                  const response: any = await this.apiService.call({
                    microServiceName: 'WSMPApplicationSettings',
                    resourcePath: `/appsrv/get-validation-rules/${currencyId}/CON/${this.sessionService.params()['profileId']}`,
                    method: 'GET'
                  });

                  const country = response.data.find(
                    (c: any) => c.countryId === simulation.countryId
                  );
                  const maxCreditAmount = country?.validations.find(
                    (r: any) => r.id === 'MPMMC'
                  );
                  if (maxCreditAmount) {
                    this.appService.restrictions.update(r => {
                      r.maxCreditAmount = Number(maxCreditAmount.value);
                      return r;
                    });
                  }
                }

                if (
                  (this.appService.restrictions().numOffersToActivate || 0) <= 0
                ) {
                  this.messageService.show(
                    $localize`You have reached the limit of active loans according to your current subscription.`,
                    {
                      type: 'modal',
                      class:
                        'text-center d-flex flex-column align-items-center gap-3',
                      icon: {
                        name: 'warning',
                        class: 'text-warning ft-icon--4',
                        collection: 'factoricons-slim'
                      },
                      actions: [
                        {
                          label: $localize`Accept`,
                          value: 1,
                          type: 'flat',
                          class: 'flex-grow-1'
                        }
                      ]
                    }
                  );
                  return;
                } else if (
                  (this.appService.restrictions()?.amountInvested?.[
                    currencyId
                  ] || 0) +
                    simulation.amount >
                  (this.appService.restrictions()?.maxCreditAmount || 0)
                ) {
                  this.messageService.show(
                    $localize`You are exceeding the maximum credit amount you can register under your current subscription.`,
                    {
                      type: 'modal',
                      class:
                        'text-center d-flex flex-column align-items-center gap-3',
                      icon: {
                        name: 'warning',
                        class: 'text-warning ft-icon--4',
                        collection: 'factoricons-slim'
                      },
                      actions: [
                        {
                          label: $localize`Accept`,
                          value: 1,
                          type: 'flat',
                          class: 'flex-grow-1'
                        }
                      ]
                    }
                  );
                } else {
                  this.active(simulation);
                }
              }
            }
          ].filter(a => a.visible)
        },
        {
          id: 'documents',
          type: 'group',
          children: [
            {
              id: 'generateDocument',
              label: $localize`Generate formal document`,
              visible: simulation.generateFormalDocument && this.sessionService.isGranted('btn-mnu-generate-formal-document'),
              click: () => {
                this.generateDocument(simulation);
              }
            }
          ].filter(a => a.visible)
        },
        {
          id: 'others',
          type: 'group',
          children: [
            {
              id: 'remove',
              label: $localize`Delete`,
              visible: this.sessionService.isGranted('btn-mnu-delete'),
              click: () => {
                this.remove(simulation);
              }
            },
            {
              id: 'download',
              label: $localize`Download PDF`,
              visible: !this.appService.isMobile && this.sessionService.isGranted('btn-mnu-download-pdf'),
              click: () => {
                this.share(simulation, true);
              }
            },
            {
              id: 'share',
              label: $localize`Share PDF`,
              visible: Capacitor.isNativePlatform() && this.sessionService.isGranted('btn-mnu-share-pdf'),
              click: () => {
                this.share(simulation);
              }
            }
          ].filter(a => a.visible)
        }
      ].filter(g => g.children.length > 0);
    }
    return actions;
  }

  /**
   * @description Genera un nombre de archivo único con timestamp para documentos
   * @returns {string} Nombre en formato offer-DDMMhhmmss.pdf
   */
  generateFilename(): string {
    const now = new Date();

    const pad = (n: number): string => n.toString().padStart(2, '0');

    const dd = pad(now.getDate());
    const MM = pad(now.getMonth() + 1); // Mes empieza en 0
    const hh = pad(now.getHours());
    const mm = pad(now.getMinutes());
    const ss = pad(now.getSeconds());

    return $localize`offer` + `-${dd}${MM}${hh}${mm}${ss}.pdf`;
  }

  /**
   * @description Genera un documento formal de la simulación
   * @param {Simulation} simulation - Simulación para generar documento
   * @returns {Promise<void>} Promesa que resuelve cuando se genera el documento
   */
  async generateDocument(simulation: Simulation): Promise<void> {
    this.progressService.show({ modal: true });
    try {
      const mimeType: string = 'application/pdf';
      const lenderId = this.sessionService.settings()?.lenderId;
      const response = await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/document/generate-formal-documents`,
        method: 'POST',
        useToken: true,
        body: { lenderId, operationNumber: simulation.operationNumber },
        bodyEncrypted: true,
        responseEncrypted: true
      });
      const base64Document = response.data.base64Document;

      // Decodificar Base64 y crear un Blob
      const binary = atob(base64Document); // Decodifica Base64
      const binaryLength = binary.length;
      const bytes = new Uint8Array(binaryLength);
      const fileName = simulation.operationNumber
        ? $localize`simulation` + `-${simulation.operationNumber}.pdf`
        : this.generateFilename();
      for (let i = 0; i < binaryLength; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType }); // Cambia el tipo MIME según el contenido del documento.
      if (this.appService.isMobile) {
        this.nativeShare(base64Document, fileName);
      } else {
        // Crear un enlace temporal y descargar el archivo
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Cambia el nombre del archivo según tus necesidades.
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Liberar el objeto URL
        URL.revokeObjectURL(url);
      }
    } finally {
      this.progressService.hide();
    }
  }

  /**
   * @description Mapea los datos de la solicitud
   * @param {any} data - Datos a mapear
   * @returns {any} Datos mapeados
   */
  mapRequest(data: any): any {
    let request = structuredClone(data);
    const lenderId = this.sessionService.settings()?.lenderId;
    request.lenderId = lenderId;
    request.subscriptionLevelId = this.sessionService.params()['profileId'];
    request.countryId = request.country.id;
    request.productTypeId = 'CON';
    request.currencyId = request.country.value;
    request.frequencyId = request.frequency.id;
    request.tableTypeId = request.tableType.id;
    request.effectiveDate =
      typeof request.effectiveDate === 'string'
        ? request.effectiveDate
        : request.effectiveDate.toLocaleDateString('en-CA');
    request.firstPaymentDate =
      typeof request.firstPaymentDate === 'string'
        ? request.firstPaymentDate
        : request.firstPaymentDate.toLocaleDateString('en-CA');
    delete request.currency;
    delete request.frequency;
    delete request.tableType;
    delete request.country;
    return request;
  }

  /**
   * @description Comparte una simulación
   * @param {Simulation} simulation - Simulación a compartir
   * @returns {Promise<void>} Promesa que resuelve cuando se comparte la simulación
   */
  async share(simulation: Simulation, download?: boolean): Promise<void> {
    const mimeType: string = 'application/pdf';
    const lenderId = this.sessionService.settings()?.lenderId;
    const isBeneficiary = this.sessionService.params()['profileId'] === PROFILE_IDS.BasicBeneficiary;
    const operationNumber = String(isBeneficiary ? simulation.operation.number : simulation.operationNumber);
    const countryId = isBeneficiary ? simulation.operation.additionalData.countryId : simulation.countryId;
    this.progressService.show({ modal: true });
    let response: any;
    try {
      response = await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/document/generate-offer-document`,
        method: 'POST',
        useToken: true,
        body: {
          lenderId,
          operationNumber,
          countryId,
          productTypeId: 'CON'
        },
        bodyEncrypted: true,
        responseEncrypted: true
      });
    } finally {
      this.progressService.hide();
    }
    const base64Document = response.data.base64Document;

    // Decodificar Base64 y crear un Blob
    const binary = atob(base64Document); // Decodifica Base64
    const binaryLength = binary.length;
    const bytes = new Uint8Array(binaryLength);
    for (let i = 0; i < binaryLength; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType }); // Cambia el tipo MIME según el contenido del documento.
    const fileName = operationNumber
      ? $localize`offer` + `-${operationNumber}.pdf`
      : this.generateFilename();

    try {
      if (!download) {
        if (Capacitor.isNativePlatform()) {
          this.nativeShare(base64Document, fileName);
          return;
        }
        const file = new File([blob], fileName, { type: mimeType });
        await navigator.share({ files: [file], title: '', text: '' });
      } else {
        // Crear un enlace temporal y descargar el archivo
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Cambia el nombre del archivo según tus necesidades.
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Liberar el objeto URL
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Sharing error:', error);
    } finally {
      //this.sharing.set(false);
    }
  }

  /**
   * @description Comparte los detalles de una simulación
   * @param {Simulation} simulation - Simulación a compartir
   * @returns {Promise<void>} Promesa que resuelve cuando se comparten los detalles
   */
  async shareSimulation(
    simulation: Simulation,
    download?: boolean
  ): Promise<void> {
    const mimeType: string = 'application/pdf';
    const response: any = await this.apiService.call({
      microServiceName: 'WSMPSimulations',
      resourcePath: `/core/generate-simulation-document`,
      method: 'POST',
      useToken: true,
      body: this.mapRequest(simulation),
      bodyEncrypted: true,
      responseEncrypted: true
    });
    const base64Document = response.data.base64Document;

    // Decodificar Base64 y crear un Blob
    const binary = atob(base64Document); // Decodifica Base64
    const binaryLength = binary.length;
    const bytes = new Uint8Array(binaryLength);
    for (let i = 0; i < binaryLength; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType }); // Cambia el tipo MIME según el contenido del documento.

    try {
      const fileName = simulation.operationNumber
        ? $localize`offer` + `-${simulation.operationNumber}.pdf`
        : this.generateFilename();
      if (Capacitor.isNativePlatform()) {
        this.nativeShare(base64Document, fileName);
        return;
      }
      const file = new File([blob], fileName, { type: mimeType });
      if (!download) {
        await navigator.share({ files: [file], title: '', text: '' });
      } else {
        // Crear un enlace temporal y descargar el archivo
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Cambia el nombre del archivo según tus necesidades.
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Liberar el objeto URL
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Sharing error:', error);
    } finally {
      //this.sharing.set(false);
    }
  }

  /**
   * @description Comparte un documento usando la API nativa de Capacitor (Filesystem + Share)
   * @param {string} data - Contenido del archivo en base64
   * @param {string} fileName - Nombre del archivo
   * @returns {Promise<void>}
   */
  async nativeShare(data: string, fileName: string): Promise<void> {
    await Filesystem.writeFile({
      path: fileName,
      data,
      directory: Directory.Cache
    });
    const uriResult = await Filesystem.getUri({
      directory: Directory.Cache,
      path: fileName
    });
    Share.share({ files: [uriResult.uri], title: '' });
  }
}
